import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { CLP, QUOTE_STATUS } from "@/lib/api";
import { PageContainer, PageHeader, StatusBadge, PrimaryButton, SecondaryButton, Field, inputCls } from "@/components/ui-kit";
import { ArrowLeft, Trash2, Plus, X, Save, Mail, Download, Cloud, ShieldCheck, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API_BASE } from "@/lib/api";
import EmailComposeModal from "@/components/EmailComposeModal";

function SendEmailModal({ quote, onClose }) {
  const [to, setTo] = useState(quote?.customer?.email || "");
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const form = new FormData();
      form.append("to", to);
      await api.post(`/quotes/${quote.id}/send-email`, form);
      toast.success("Cotización enviada");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md border-t-4 border-[#FF5A00]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div className="font-display font-bold text-lg">Enviar cotización por email</div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          <label className="block text-xs font-mono-kpi uppercase tracking-[0.2em] text-neutral-500 mb-2">Destinatario</label>
          <input type="email" required data-testid="quote-email-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2 border border-neutral-300 focus:border-[#FF5A00] outline-none text-sm" placeholder="cliente@ejemplo.cl" />
          <div className="text-xs text-neutral-500 mt-3">Se enviará desde la cuenta Google conectada con el detalle completo de la cotización y los totales.</div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={sending} data-testid="quote-email-send">
            <Mail className="w-3.5 h-3.5 inline mr-2 -mt-0.5" />{sending ? "Enviando…" : "Enviar"}
          </PrimaryButton>
        </div>
      </form>
    </div>
  );
}

export default function QuoteDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [q, setQ] = useState(null);
  const [showEmail, setShowEmail] = useState(false);
  const isAdmin = user?.role === "admin";

  const load = async () => { const { data } = await api.get(`/quotes/${id}`); setQ(data); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!q) return <PageContainer><div className="font-mono-kpi text-xs text-neutral-500 uppercase tracking-[0.2em]">Cargando…</div></PageContainer>;

  const updateItem = (idx, k, v) => {
    const items = [...q.items];
    items[idx] = { ...items[idx], [k]: v };
    setQ({ ...q, items });
  };

  const save = async () => {
    await api.patch(`/quotes/${id}`, {
      title: q.title, status: q.status, tax_rate: q.tax_rate, notes: q.notes,
      valid_until: q.valid_until,
      items: q.items.map((i) => ({
        ...i,
        quantity: Number(i.quantity),
        unit_price: Number(i.unit_price),
        internal_cost: Number(i.internal_cost || 0),
      })),
      photos: q.photos || [],
    });
    toast.success("Cotización actualizada");
    load();
  };

  const toBase64 = (file) =>
    new Promise((res, rej) => {
      const r = new FileReader();
      r.onload = () => res(r.result);
      r.onerror = rej;
      r.readAsDataURL(file);
    });
  const handlePhotos = async (files) => {
    if (!files?.length) return;
    const encoded = await Promise.all(Array.from(files).map(async (f) => ({ data: await toBase64(f), caption: "" })));
    setQ({ ...q, photos: [...(q.photos || []), ...encoded] });
  };
  const updatePhotoCaption = (idx, caption) => {
    setQ({ ...q, photos: q.photos.map((p, i) => (i === idx ? { ...p, caption } : p)) });
  };
  const removePhoto = (idx) => {
    setQ({ ...q, photos: q.photos.filter((_, i) => i !== idx) });
  };

  const remove = async () => {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    await api.delete(`/quotes/${id}`);
    toast.success("Eliminada");
    navigate("/admin/cotizaciones");
  };

  const downloadPDF = async () => {
    try {
      const res = await fetch(`${API_BASE}/quotes/${id}/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Cotizacion_${q.number}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error generando PDF");
    }
  };

  const uploadToDrive = async () => {
    try {
      const { data } = await api.post(`/quotes/${id}/upload-drive`);
      toast.success("Cotización subida a Drive");
      if (data.webViewLink) window.open(data.webViewLink, "_blank");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error subiendo a Drive");
    }
  };

  return (
    <PageContainer>
      <Link to="/admin/cotizaciones" data-testid="quote-back" className="inline-flex items-center gap-1.5 text-xs font-mono-kpi uppercase tracking-[0.2em] text-neutral-500 hover:text-[#FF5A00] mb-4">
        <ArrowLeft className="w-3 h-3" /> Volver a cotizaciones
      </Link>
      <PageHeader
        kicker={q.number}
        title={q.title}
        subtitle={q.customer?.name}
        actions={
          <div className="flex gap-2 flex-wrap">
            <SecondaryButton data-testid="quote-download-pdf" onClick={downloadPDF}>
              <Download className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> PDF
            </SecondaryButton>
            <SecondaryButton data-testid="quote-upload-drive" onClick={uploadToDrive}>
              {q.drive_file ? <Cloud className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5 text-[#00C853]" /> : <Cloud className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />}
              {q.drive_file ? "En Drive" : "Subir a Drive"}
            </SecondaryButton>
            <SecondaryButton data-testid="quote-send-email" onClick={() => setShowEmail(true)}>
              <Mail className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Email
            </SecondaryButton>
            <PrimaryButton data-testid="quote-save" onClick={save}>
              <Save className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" /> Guardar
            </PrimaryButton>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-neutral-200 p-6">
          {q.warranty_until && q.status === "approved" && (() => {
            const today = new Date(); today.setHours(0,0,0,0);
            const wu = new Date(q.warranty_until);
            const days = Math.ceil((wu - today) / 86400000);
            const active = days >= 0;
            return (
              <div
                data-testid="quote-warranty-banner"
                className={`mb-6 flex items-center gap-3 px-4 py-3 border ${active ? "bg-[#00C853]/5 border-[#00C853]/30 text-[#008C3A]" : "bg-neutral-100 border-neutral-300 text-neutral-500"}`}
              >
                <ShieldCheck className="w-4 h-4" />
                <div className="text-sm">
                  <span className="font-mono-kpi text-[10px] uppercase tracking-[0.18em] mr-2">
                    {active ? "Garantía activa" : "Garantía vencida"}
                  </span>
                  {active
                    ? <>Vence el <b>{q.warranty_until}</b> · faltan <b>{days} días</b> (90 días desde aprobación)</>
                    : <>Venció el <b>{q.warranty_until}</b></>}
                </div>
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Field label="Título"><input value={q.title} onChange={(e) => setQ({ ...q, title: e.target.value })} className={inputCls} /></Field>
            <Field label="Estado">
              <select value={q.status} onChange={(e) => setQ({ ...q, status: e.target.value })} className={inputCls}>
                {QUOTE_STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="IVA (%)"><input type="number" value={q.tax_rate} onChange={(e) => setQ({ ...q, tax_rate: Number(e.target.value) })} className={inputCls} /></Field>
            <Field label="Válida hasta"><input type="date" value={q.valid_until || ""} onChange={(e) => setQ({ ...q, valid_until: e.target.value })} className={inputCls} /></Field>
          </div>

          <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00] mb-2">// Ítems</div>
          <div className="border border-neutral-200">
            <div className={`grid ${isAdmin ? "grid-cols-[1fr_70px_110px_110px_120px_40px]" : "grid-cols-[1fr_80px_120px_120px_40px]"} gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500`}>
              <div>Descripción</div><div className="text-right">Cant.</div>
              {isAdmin && <div className="text-right">Costo interno</div>}
              <div className="text-right">Precio</div><div className="text-right">Total</div><div />
            </div>
            {q.items.map((it, i) => {
              const lineTotal = Number(it.quantity || 0) * Number(it.unit_price || 0);
              const lineCost = Number(it.quantity || 0) * Number(it.internal_cost || 0);
              const lineMargin = lineTotal - lineCost;
              return (
                <div key={i} className={`grid ${isAdmin ? "grid-cols-[1fr_70px_110px_110px_120px_40px]" : "grid-cols-[1fr_80px_120px_120px_40px]"} gap-2 px-3 py-2 border-b border-neutral-100 items-center`}>
                  <div>
                    <input value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className={inputCls} data-testid={`quote-item-desc-${i}`} />
                    {isAdmin && Number(it.internal_cost) > 0 && (
                      <div className="mt-1 text-[10px] font-mono-kpi uppercase tracking-wider text-neutral-400">
                        Margen: <span className={lineMargin >= 0 ? "text-[#00C853]" : "text-[#D50000]"}>{CLP(lineMargin)}</span>
                        {lineTotal > 0 && ` (${((lineMargin / lineTotal) * 100).toFixed(1)}%)`}
                      </div>
                    )}
                  </div>
                  <input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className={`${inputCls} text-right`} />
                  {isAdmin && (
                    <input type="number" min="0" value={it.internal_cost || 0} onChange={(e) => updateItem(i, "internal_cost", e.target.value)}
                      data-testid={`quote-item-cost-${i}`}
                      className={`${inputCls} text-right bg-[#FFE0B2]/30`} placeholder="0" />
                  )}
                  <input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className={`${inputCls} text-right`} />
                  <div className="font-mono-kpi text-right text-sm">{CLP(lineTotal)}</div>
                  <button onClick={() => setQ({ ...q, items: q.items.filter((_, idx) => idx !== i) })} className="hover:bg-neutral-100 p-1.5"><X className="w-3 h-3" /></button>
                </div>
              );
            })}
            <button onClick={() => setQ({ ...q, items: [...q.items, { description: "", quantity: 1, unit_price: 0, internal_cost: 0 }] })} className="w-full text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#FF5A00] hover:bg-[#FF5A00]/5 py-2">
              <Plus className="w-3 h-3 inline mr-1" /> Agregar ítem
            </button>
          </div>

          <div className="mt-4">
            <Field label="Notas">
              <textarea rows={3} value={q.notes || ""} onChange={(e) => setQ({ ...q, notes: e.target.value })} className={inputCls} />
            </Field>
          </div>

          {/* Fotos adjuntas */}
          <div className="mt-6 pt-6 border-t border-neutral-100">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">
                Fotos adjuntas ({(q.photos || []).length})
              </div>
              <label className="cursor-pointer inline-flex items-center gap-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:text-[#E04A00]" data-testid="quote-detail-add-photos">
                <span>+ Agregar fotos</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="quote-detail-photos-input"
                  onChange={(e) => handlePhotos(e.target.files)}
                />
              </label>
            </div>
            {(q.photos || []).length === 0 ? (
              <div className="border border-dashed border-neutral-300 px-3 py-6 text-xs text-neutral-500 text-center">
                Sin fotos. Adjunta imágenes que se incluirán al descargar el PDF.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {q.photos.map((p, i) => (
                  <div key={i} className="border border-neutral-200 p-2" data-testid={`quote-detail-photo-${i}`}>
                    <img src={p.data} alt="" className="w-full h-28 object-cover mb-2" />
                    <input
                      value={p.caption || ""}
                      onChange={(e) => updatePhotoCaption(i, e.target.value)}
                      placeholder="Descripción…"
                      className="w-full text-xs border border-neutral-200 px-2 py-1 mb-1"
                    />
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`quote-detail-photo-remove-${i}`} className="w-full text-[10px] font-mono-kpi uppercase tracking-wider text-[#B71C1C] hover:bg-[#B71C1C]/5 py-1">
                      Quitar
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="text-[10px] text-neutral-400 mt-2">Los cambios se guardan al hacer clic en Guardar.</div>
          </div>
        </div>

        <aside className="space-y-4">
          <div className="bg-[#0A0A0A] text-white p-6">
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-400">// Totales</div>
            <div className="mt-4 space-y-2">
              <div className="flex justify-between text-sm"><span className="text-neutral-300">Subtotal</span><span className="font-mono-kpi font-bold">{CLP(q.subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-neutral-300">IVA ({q.tax_rate}%)</span><span className="font-mono-kpi font-bold">{CLP(q.tax)}</span></div>
              <div className="flex justify-between pt-2 border-t border-neutral-800"><span className="text-sm">Total</span><span className="font-mono-kpi font-bold text-xl text-[#FF5A00]">{CLP(q.total)}</span></div>
            </div>
            <div className="mt-4"><StatusBadge status={q.status} /></div>
          </div>

          {isAdmin && (q.internal_cost || 0) > 0 && (
            <div className="bg-white border border-neutral-200 p-6" data-testid="quote-margin-card">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp className="w-4 h-4 text-[#FF5A00]" />
                <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">// Análisis de margen (admin)</div>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">Precio (neto)</span><span className="font-mono-kpi font-bold">{CLP(q.subtotal)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-500">Costo interno</span><span className="font-mono-kpi font-bold text-[#D50000]">− {CLP(q.internal_cost)}</span></div>
                <div className="flex justify-between pt-2 border-t border-neutral-200">
                  <span className="font-medium">Margen</span>
                  <span className={`font-mono-kpi font-bold text-lg ${q.margin >= 0 ? "text-[#00C853]" : "text-[#D50000]"}`} data-testid="quote-margin-value">
                    {CLP(q.margin)} <span className="text-xs opacity-70">({q.margin_pct}%)</span>
                  </span>
                </div>
              </div>
              <div className="mt-3 text-[10px] text-neutral-400 font-mono-kpi uppercase tracking-wider">
                Solo visible para administradores
              </div>
            </div>
          )}

          {q.customer && (
            <Link to={`/admin/clientes/${q.customer.id}`} className="block bg-white border border-neutral-200 hover:border-[#0A0A0A] p-5">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Cliente</div>
              <div className="font-display font-bold text-base mt-1">{q.customer.name}</div>
              <div className="text-xs text-neutral-500 mt-1">{q.customer.address}</div>
            </Link>
          )}

          <button onClick={remove} data-testid="quote-delete" className="w-full text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#D50000] hover:bg-[#D50000]/5 border border-[#D50000]/30 px-3 py-2.5">
            <Trash2 className="w-3 h-3 inline mr-1.5" /> Eliminar
          </button>
        </aside>
      </div>
      {showEmail && (
        <EmailComposeModal
          onClose={() => setShowEmail(false)}
          quoteId={q.id}
          customerId={q.customer_id}
          defaultTemplate="quote"
          allowedTemplates={["quote", "report_and_quote", "custom"]}
        />
      )}
    </PageContainer>
  );
}
